/**
 * Global Error Handler
 * Catches unhandled JavaScript errors and promise rejections
 */

export type ErrorType = 'javascript' | 'server';

interface HttpErrorContext {
    url?: string;
    method?: string;
    status?: number;
}

// Reactive state for error overlay visibilityOKay
let errorVisible = $state(false);
let errorMessage = $state<string | null>(null);
let errorStack = $state<string | null>(null);
let errorType = $state<ErrorType>('javascript');
let httpContext = $state<HttpErrorContext | null>(null);

export const errorState = {
    get visible() {
        return errorVisible;
    },
    get message() {
        return errorMessage;
    },
    get stack() {
        return errorStack;
    },
    get type() {
        return errorType;
    },
    get httpContext() {
        return httpContext;
    },
};

/**
 * Show the error overlay with a message
 */
export function showErrorOverlay(error: Error | string, type: ErrorType = 'javascript', context?: HttpErrorContext) {
    const message = error instanceof Error ? error.message : error;
    const stack = error instanceof Error ? error.stack : null;

    console.error('[Global Error Handler]', error, {
        message: error instanceof Error ? error.message : error,
        stack: error instanceof Error ? error.stack : 'no stack',
        raw: JSON.stringify(error),
    });

    errorMessage = message;
    errorStack = stack ?? null;
    errorType = type;
    httpContext = context ?? null;
    errorVisible = true;
}

/**
 * Show the error overlay for HTTP 500 errors
 */
export function showServerErrorOverlay(message: string, context: HttpErrorContext) {
    showErrorOverlay(message, 'server', context);
}

/**
 * Hide the error overlay (user chose to continue)
 */
export function hideErrorOverlay() {
    errorVisible = false;
    errorMessage = null;
    errorStack = null;
    errorType = 'javascript';
    httpContext = null;
}

/**
 * Reload the page
 */
export function reloadPage() {
    window.location.reload();
}

/**
 * Initialize global error handlers
 * Call this once at app startup
 */
export function initErrorHandler() {
    // Handle uncaught errors - use addEventListener with capture to catch errors
    // before frameworks like Svelte can swallow them
    window.addEventListener(
        'error',
        (event: ErrorEvent) => {
            // Ignore errors from browser extensions
            if (event.filename?.includes('extension://')) {
                return;
            }

            // Ignore errors we've already handled
            if (errorVisible) {
                return;
            }

            // Ignore this non-critical warning
            if (event.message?.includes('ResizeObserver loop')) {
                return;
            }

            // Ignore resource loading errors (they don't have useful stack traces)
            if (event.target && event.target !== window) {
                console.warn('[Resource Load Error]', {
                    element: (event.target as HTMLElement).tagName,
                    src: (event.target as HTMLImageElement).src || (event.target as HTMLScriptElement).src || (event.target as HTMLLinkElement).href,
                });
                return; // Don't show overlay for resource errors
            }
            showErrorOverlay(event.error ?? new Error(event.message));
        },
        true,
    ); // capture phase

    // Also keep the onerror handler for compatibility
    window.onerror = (message, source, lineno, colno, error) => {
        // Ignore errors from browser extensions
        if (typeof source === 'string' && source.includes('extension://')) {
            return false;
        }

        // Ignore if already showing an error
        if (errorVisible) {
            return true;
        }

        showErrorOverlay(error ?? String(message));
        return true;
    };

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const reason = event.reason;

        // Ignore cancelled requests
        if (reason?.name === 'AbortError' || reason?.name === 'CancelledError') {
            return;
        }

        // Ignore if already showing an error
        if (errorVisible) {
            return;
        }

        const message = reason instanceof Error ? reason : new Error(String(reason ?? 'Unknown promise rejection'));

        showErrorOverlay(message);
    });
}
